/**
 * formRouting — (visa_subclass, role, variant) → form_id.
 *
 * Split out of loadSchema.ts because that module calls Vite's `import.meta.glob`
 * at import time, which makes it unusable from a plain Node script. The routing
 * itself is pure, and scripts (e.g. scripts/backfill-agent-details.ts) need it to
 * decide which form a given matter uses. Keeping ONE copy is the point: a second,
 * script-local mapping would drift from the app and silently backfill the wrong form.
 *
 * loadSchema.ts re-exports both functions, so existing imports are unaffected.
 */

import type { FormId } from '../../schema/generated/index';

/**
 * Subclasses that have a separate second-party form (partner visas use 40SP;
 * the 600 family-sponsored stream uses Form 1149; the 600 tourist stream uses
 * ADI's own light Inviter form). Everything else has no second party form:
 * e.g. 461 (NZ Citizen Family Relationship) captures the NZ citizen inline in
 * Form 147 Parts E/F — there is no 40SP sponsorship for it. Single source of
 * truth for gating the sponsor/inviter role, intake and PDF across the app.
 *
 * APPLICABILITY, NOT REQUIREDNESS. This set answers "if this matter has a
 * second party, do they get their own form?". It deliberately does NOT answer
 * "must this matter have a second party?". See isSponsorRequired below. The
 * 600 tourist Inviter is OPTIONAL (Tzvi ruling 2026-07-24, Wave 5): a tourist
 * matter with no inviter must behave exactly as it did before this set grew.
 */
const SPONSOR_FORM_VISAS = new Set([
  '820',
  '801',
  '309',
  '100',
  '300',
  '600-family',
  '600-tourist',
]);

export function hasSponsorForm(visaSubclass: string): boolean {
  return SPONSOR_FORM_VISAS.has(visaSubclass);
}

/**
 * Subclasses that REQUIRE a second party on the matter.
 *
 * Split out of hasSponsorForm in Wave 5, because that helper had quietly come
 * to mean two different things: "a second party gets their own form" AND "a
 * second party is mandatory". The 600 tourist Inviter is optional, so the two
 * meanings now diverge and every gate that BLOCKS on a missing party must use
 * this helper instead.
 *
 * 461 is in this set and NOT in SPONSOR_FORM_VISAS: the NZ citizen family
 * member is a required party on the matter, but their details are captured on
 * the applicant's own Form 147 rather than a separate form. That is exactly
 * today's behaviour (MatterReadiness carried its own copy of this list and
 * CreateMatter derived it from the presence of a sponsor label); this is the
 * single source of truth for it.
 */
const SPONSOR_REQUIRED_VISAS = new Set([
  '820',
  '801',
  '309',
  '100',
  '300',
  '600-family',
  '461',
]);

export function isSponsorRequired(visaSubclass: string): boolean {
  return SPONSOR_REQUIRED_VISAS.has(visaSubclass);
}

/**
 * Client- and staff-facing noun for the second party on a matter.
 *
 * Vocabulary rule (Tzvi ruling 2026-07-24): the 600 tourist stream says
 * "Inviter" everywhere a human can read it; every other sponsor-bearing
 * subclass keeps "Sponsor". Internal canonical names, table columns
 * (matters.sponsor_contact_id) and signal names (hasInviter,
 * application.has_supporters) are deliberately unchanged: this is a label,
 * not a rename.
 */
export function sponsorRoleLabel(visaSubclass: string): 'Inviter' | 'Sponsor' {
  return visaSubclass === '600-tourist' ? 'Inviter' : 'Sponsor';
}

/** Lower-case form of sponsorRoleLabel, for mid-sentence copy. */
export function sponsorRoleNoun(visaSubclass: string): 'inviter' | 'sponsor' {
  return visaSubclass === '600-tourist' ? 'inviter' : 'sponsor';
}

/**
 * Mapping from (visa_subclass, isSponsor) → form_id.
 *
 * Covers all 9 primary schemas. Stage 2 is mapped from variant === 'stage2'
 * or the 801/100 subclasses.
 */
export function pickFormId(
  visaSubclass: string,
  isSponsor: boolean,
  variant?: string | null,
): FormId | null {
  if (isSponsor) {
    // No second-party form exists for this visa (461, 020). Return null rather
    // than falling through to 40SP: a NZ-family/BVB "sponsor" was being served
    // the partner-visa sponsorship form, and the intake bootstrap then
    // PERSISTED a 40SP row against the matter. hasSponsorForm is the single
    // source of truth, and this function must never contradict it.
    if (!hasSponsorForm(visaSubclass)) return null;

    // 600 family-sponsored uses the visitor-sponsor form (Form 1149); the 600
    // tourist stream uses ADI's own light Inviter form (no Home Affairs form
    // exists for an inviter); all other sponsors are partner-visa sponsors and
    // use 40SP.
    if (visaSubclass === '600-family') return '1149_family_visitor_sponsor';
    if (visaSubclass === '600-tourist') return '600_tourist_inviter';
    return '40SP_sponsor';
  }

  // Stage-2 variants (801 onshore permanent, 100 offshore permanent)
  if (visaSubclass === '801' || visaSubclass === '100') return '47SP_stage2';
  if ((visaSubclass === '820' || visaSubclass === '309') && variant === 'stage2') {
    return '47SP_stage2';
  }

  switch (visaSubclass) {
    case '820': return '47SP_820_onshore_stage1';
    case '309': return '47SP_309_offshore_stage1';
    case '300': return '47SP_300_PMV';
    case '461': return '147_nz_family';
    case '020': return '1006_bridging_visa_b';
    case '600-tourist': return '1419_tourist';
    case '600-family': return '1418_family_sponsored';
    default:
      // Partner-visa fallback
      return '47SP_820_onshore_stage1';
  }
}
